import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowUpDown, BadgeCheck, BookOpen, Calendar, Coffee, Flame, Heart, LayoutGrid, List,
  Lock, MapPin, Plus, Search, SlidersHorizontal, Sparkles, Star, Users, X, ChevronDown, Check, Globe2
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import EventCard from '../components/events/EventCard';
import EventFilterPanel from '../components/events/EventFilterPanel';
import { UserContext } from '../App';
import { useEvents } from '../hooks/useEvents';
import { supabase } from '../utils/supabaseClient';
import { EVENT_CATEGORIES, SPORT_CATEGORY } from '../utils/eventCategories';
import { EVENT_CITIES, TAIWAN_CITIES } from '../utils/taiwanCities';
import { groupEventsByTime, toDateString } from '../utils/eventDateFilters';
import { SECTION_THEME } from '../utils/sectionTheme';
import BannerScene from '../components/common/BannerScene';
import { createEventTestFixtures } from '../test/fixtures/eventFixtures';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { compareTrendingEvents, splitDiscoverySections } from '../utils/eventDiscovery';
import { useMobileHeader } from '../context/MobileHeaderContext';
import FullScreenFilterModal from '../components/common/FullScreenFilterModal';
const cardGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
  gap: '18px'
};

const listGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '12px'
};

const desktopGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px'
};

const CATEGORY_ICONS = {
  美食: Coffee,
  文藝: Heart,
  聚會: Users,
  戶外: MapPin,
  學習: BookOpen,
};

const pillStyle = (isSelected) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '9px 14px',
  color: isSelected ? '#ffffff' : '#334155',
  background: isSelected ? SECTION_THEME.events.accent : '#ffffff',
  border: isSelected ? `1px solid ${SECTION_THEME.events.accent}` : '1px solid #dbe4ee',
  borderRadius: '999px',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: '750',
  cursor: 'pointer',
  whiteSpace: 'nowrap'
});

const selectStyle = {
  padding: '9px 12px',
  color: '#334155',
  background: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: '12px',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: '700',
  cursor: 'pointer'
};

const dateInputStyle = {
  boxSizing: 'border-box',
  minHeight: '38px',
  padding: '8px 10px',
  color: '#334155',
  background: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: '10px',
  fontFamily: 'inherit',
  fontSize: '13px'
};

export default function Events() {
  const { currentUser, blockedIds } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isDemoMode = process.env.NODE_ENV !== 'production' && searchParams.get('demo') === '1';
  const currentUserId = currentUser?._id || currentUser?.id || currentUser?.auth0Id;
  const { data, isLoading: isEventsLoading, refetch: refetchEvents } = useEvents(currentUserId, blockedIds);

  const events = useMemo(
    () => isDemoMode ? createEventTestFixtures() : (data?.events || []),
    [data?.events, isDemoMode]
  );
  const userFavorites = useMemo(() => data?.userFavorites || [], [data?.userFavorites]);
  const userFriends = useMemo(() => data?.userFriends || [], [data?.userFriends]);
  const isLoading = isDemoMode ? false : isEventsLoading;

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedCities, setSelectedCities] = useState([]);
  const [dateStartFilter, setDateStartFilter] = useState('');
  const [dateEndFilter, setDateEndFilter] = useState('');
  const [filterType, setFilterType] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const isDesktop = useIsDesktop();
  const { setHeaderLeftAction } = useMobileHeader();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [sortMode, setSortMode] = useState('latest');
  const [viewMode, setViewMode] = useState('grid');

  const categories = EVENT_CATEGORIES;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSelectedCategory(params.get('category') || '');
    setSearchQuery(params.get('q') || '');
  }, [location.search]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('create') === '1') {
      navigate('/events/create', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!currentUserId) return undefined;
    const channel = supabase
      .channel('my-participant-status-change')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'event_participants',
        filter: `user_id=eq.${currentUserId}`
      }, () => refetchEvents())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [currentUserId, refetchEvents]);

  const favoriteEventIds = useMemo(() => new Set(userFavorites.map(id => id.toString())), [userFavorites]);
  const friendIds = useMemo(() => new Set(userFriends.map(id => id.toString())), [userFriends]);
  const today = toDateString(new Date());

  const filteredEvents = events.filter(event => {
    if (!event.date || event.date < today || event.category === SPORT_CATEGORY) return false;

    const keyword = searchQuery.trim().toLowerCase();
    const searchableText = [event.title, event.description, event.location, event.address, event.city, event.category]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesSearch = !keyword || searchableText.includes(keyword);
    const matchesCategory = !selectedCategory || event.category === selectedCategory;
    const matchesDate = (!dateStartFilter || event.date >= dateStartFilter) &&
      (!dateEndFilter || event.date <= dateEndFilter);
    const matchesCity = selectedCities.length === 0 || (
      event.city
        ? selectedCities.includes(event.city)
        : selectedCities.some(city => event.location?.includes(city) || event.address?.includes(city))
    );

    let matchesType = true;
    if (filterType === 'favorites') {
      matchesType = favoriteEventIds.has((event._id || event.id || '').toString());
    } else if (filterType === 'organized') {
      const organizerIds = [event.organizer_id, event.organizer?._id, event.organizer?.id, event.organizer?.auth0Id]
        .filter(Boolean)
        .map(id => id.toString());
      matchesType = Boolean(currentUserId) && organizerIds.includes(currentUserId.toString());
    } else if (filterType === 'joined') {
      const participantIds = (event.participants || [])
        .map(participant => typeof participant === 'object'
          ? participant.user_id || participant._id || participant.id || participant.auth0Id
          : participant)
        .filter(Boolean)
        .map(id => id.toString());
      matchesType = Boolean(currentUserId) && participantIds.includes(currentUserId.toString());
    } else if (filterType === 'friends') {
      const organizerIds = [event.organizer_id, event.organizer?._id, event.organizer?.id, event.organizer?.auth0Id]
        .filter(Boolean)
        .map(id => id.toString());
      matchesType = organizerIds.some(id => friendIds.has(id));
    }

    return matchesSearch && matchesCategory && matchesDate && matchesCity && matchesType;
  }).sort((first, second) => sortMode === 'popular'
    ? compareTrendingEvents(first, second)
    : (first.date || '').localeCompare(second.date || '') || (second.participants?.length || 0) - (first.participants?.length || 0)
  );

  const {
    recommendations: recommendedEvents,
    trending: trendingEvents,
    upcoming: upcomingEvents
  } = splitDiscoverySections(filteredEvents);
  const activeFilterCount = [
    selectedCategory !== '',
    selectedCities.length > 0,
    dateStartFilter !== '' || dateEndFilter !== '',
    filterType !== ''
  ].filter(Boolean).length;

  const clearFilters = React.useCallback(() => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedCities([]);
    setDateStartFilter('');
    setDateEndFilter('');
    setFilterType('');
  }, []);

  const mobileFilterContent = useMemo(() => (
    <EventFilterPanel
      categories={categories}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      selectedCities={selectedCities}
      setSelectedCities={setSelectedCities}
      dateStartFilter={dateStartFilter}
      setDateStartFilter={setDateStartFilter}
      dateEndFilter={dateEndFilter}
      setDateEndFilter={setDateEndFilter}
      filterType={filterType}
      setFilterType={setFilterType}
      activeFilterCount={activeFilterCount}
      searchQuery={searchQuery}
      onClear={clearFilters}
      isMobile
      variant="drawer"
    />
  ), [activeFilterCount, categories, clearFilters, dateEndFilter, dateStartFilter, filterType, searchQuery, selectedCategory, selectedCities]);

  useEffect(() => {
    if (isDesktop) {
      setHeaderLeftAction(null);
      return;
    }

    const currentCityText = selectedCities.length > 0
      ? (selectedCities.length === 1 ? selectedCities[0] : `已選 ${selectedCities.length} 縣市`)
      : '全台灣';

    setHeaderLeftAction(
      <button
        onClick={() => setIsCityModalOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          padding: '8px 12px 8px 4px',
          fontFamily: 'inherit',
          fontSize: '16px',
          fontWeight: '800',
          color: '#1e293b',
          cursor: 'pointer'
        }}
      >
        <MapPin size={18} strokeWidth={2.5} color="#1e293b" />
        <span style={{ marginLeft: '6px' }}>{currentCityText}</span>
        <ChevronDown size={18} color="#64748b" />
      </button>
    );

    return () => setHeaderLeftAction(null);
  }, [isDesktop, selectedCities, setHeaderLeftAction]);

  const renderEventCard = (event, extraProps = {}) => (
    <EventCard
      key={event.id || event._id}
      event={event}
      currentUserId={currentUserId}
      onJoin={() => refetchEvents()}
      userFavorites={userFavorites}
      onFavoriteToggle={() => refetchEvents()}
      onEventCancel={() => refetchEvents()}
      onEventLeave={() => refetchEvents()}
      onEventEdit={() => navigate(`/events/${event._id || event.id}/edit`)}
      onParticipantStatusChange={() => refetchEvents()}
      {...extraProps}
    />
  );

  const gridStyle = viewMode === 'list' ? listGridStyle : (isDesktop ? desktopGridStyle : cardGridStyle);

  const quickActions = [
    { label: '我發起的活動', value: 'organized', icon: Calendar },
    { label: '收藏清單', value: 'favorites', icon: Star },
    { label: '參加中', value: 'joined', icon: Users },
  ];

  const renderDesktopFilterBar = () => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <button type="button" onClick={() => setSelectedCategory('')} style={pillStyle(selectedCategory === '')}>全部</button>
        {categories.map(category => {
          const Icon = CATEGORY_ICONS[category];
          const isSelected = selectedCategory === category;
          return (
            <button key={category} type="button" onClick={() => setSelectedCategory(isSelected ? '' : category)} style={pillStyle(isSelected)}>
              {Icon && <Icon size={14} />}{category}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
        <select aria-label="篩選縣市" value={selectedCities[0] || ''} onChange={event => setSelectedCities(event.target.value ? [event.target.value] : [])} style={selectStyle}>
          <option value="">全部縣市</option>
          {EVENT_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="date" aria-label="篩選開始日期" value={dateStartFilter} max={dateEndFilter || undefined} onChange={event => setDateStartFilter(event.target.value)} style={dateInputStyle} />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
          <input type="date" aria-label="篩選結束日期" value={dateEndFilter} min={dateStartFilter || undefined} onChange={event => setDateEndFilter(event.target.value)} style={dateInputStyle} />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {quickActions.map(action => {
            const isActive = filterType === action.value;
            return (
              <button
                key={action.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => setFilterType(previous => previous === action.value ? '' : action.value)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 12px', color: isActive ? '#376ba1' : '#475569', background: isActive ? '#eef4f9' : '#f8fafc', border: isActive ? '1px solid #9cc2e6' : '1px solid #eef2f6', borderRadius: '10px', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <action.icon size={14} />{action.label}
              </button>
            );
          })}

          <div style={{ width: '1px', height: '22px', background: '#e2e8f0', margin: '0 4px' }} />

          <button type="button" onClick={() => setSortMode(mode => mode === 'latest' ? 'popular' : 'latest')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 14px', color: SECTION_THEME.events.accent, background: '#eef4f9', border: '1px solid #dbe4ee', borderRadius: '12px', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer' }}>
            <ArrowUpDown size={14} />{sortMode === 'latest' ? '最新' : '熱門'}
          </button>
          <div style={{ display: 'flex', border: '1px solid #dbe4ee', borderRadius: '12px', overflow: 'hidden' }}>
            <button type="button" aria-label="格狀顯示" aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', color: viewMode === 'grid' ? '#ffffff' : '#526579', background: viewMode === 'grid' ? SECTION_THEME.events.accent : '#eef4f9', border: 'none', cursor: 'pointer' }}><LayoutGrid size={16} /></button>
            <button type="button" aria-label="列表顯示" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', color: viewMode === 'list' ? '#ffffff' : '#526579', background: viewMode === 'list' ? SECTION_THEME.events.accent : '#eef4f9', border: 'none', cursor: 'pointer' }}><List size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );

  const isSocialEnabled = currentUser?.profile?.is_social_enabled ?? true;
  if (!isSocialEnabled) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 20px 100px', textAlign: 'center', background: 'linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', marginBottom: '24px', color: '#ef4444', background: '#fef2f2', borderRadius: '50%' }}><Lock size={40} /></div>
        <h2 style={{ margin: '0 0 12px', color: '#1f2937', fontSize: '1.5rem', fontWeight: '800' }}>開啟交友模式，探索更多活動！</h2>
        <p style={{ maxWidth: '320px', margin: '0 0 32px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6' }}>開啟交友模式後，就能瀏覽與發起各種有趣的社交活動。</p>
        <button type="button" onClick={() => navigate('/profile')} style={{ padding: '14px 28px', color: '#ffffff', background: SECTION_THEME.events.accent, border: 'none', borderRadius: '999px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>前往開啟交友模式</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', width: '100%', paddingBottom: '90px', overflowX: 'clip', background: 'linear-gradient(180deg, #eef4f9 0%, #f5f9fc 18%, #ffffff 48%)' }}>
      <header style={{ position: 'relative', overflow: 'hidden', minHeight: isDesktop ? '176px' : '140px', padding: isMobile ? '18px 20px 20px' : (isDesktop ? '20px 32px 22px' : '24px 32px 26px'), background: SECTION_THEME.events.bannerBase, borderRadius: '0 0 32px 32px' }}>
        <BannerScene src={SECTION_THEME.events.bannerImage} alt="活動狗狗吉祥物" isMobile={isMobile} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: isDesktop ? '1360px' : '1200px', margin: '0 auto' }}>
          <div style={{ maxWidth: isDesktop ? '58%' : '100%' }}>
            <div style={{ minWidth: 0, maxWidth: isDesktop ? '100%' : '75%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <h1 style={{ margin: 0, fontSize: isDesktop ? '1.875rem' : '1.5rem', fontWeight: '700', letterSpacing: '-0.01em', lineHeight: '1.25' }}>
                  <span style={{ color: '#1e2f42' }}>找到</span><span style={{ color: SECTION_THEME.events.accent }}>一起體驗</span><span style={{ color: '#1e2f42' }}>的人</span>
                </h1>
              </div>
              <p style={{ margin: 0, color: '#4c6d8c', fontSize: isDesktop ? '0.9375rem' : '0.875rem', fontWeight: '500', lineHeight: '1.5' }}>
                因為共同興趣，相遇變得更有趣
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <Search size={17} color="#94a3b8" style={{ position: 'absolute', top: '50%', left: '14px', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input type="search" aria-label="搜尋活動" placeholder="搜尋活動..." value={searchQuery} onChange={event => setSearchQuery(event.target.value)} style={{ boxSizing: 'border-box', width: '100%', minHeight: '46px', padding: searchQuery ? '11px 42px' : '11px 14px 11px 42px', color: '#0f172a', background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(255,255,255,0.72)', borderRadius: '15px', outline: 'none', boxShadow: '0 8px 22px rgba(30,64,99,0.16)', fontFamily: 'inherit', fontSize: '14px' }} />
                {searchQuery && <button type="button" aria-label="清除活動搜尋" onClick={() => setSearchQuery('')} style={{ position: 'absolute', top: '50%', right: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', padding: 0, color: '#64748b', background: '#eef4f9', border: 'none', borderRadius: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}><X size={14} /></button>}
              </div>
              {isDesktop ? (
                <button type="button" aria-label="搜尋活動" style={{ flexShrink: 0, minWidth: '84px', height: '46px', padding: '0 22px', color: '#ffffff', background: SECTION_THEME.events.accent, border: 'none', borderRadius: '15px', fontSize: '14px', fontWeight: '800', cursor: 'pointer' }}>搜尋</button>
              ) : (
                <button type="button" aria-label="篩選活動" onClick={() => setIsFilterModalOpen(true)} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '46px', minWidth: '46px', height: '46px', padding: 0, color: activeFilterCount > 0 ? '#376ba1' : '#526579', background: activeFilterCount > 0 ? '#eef4f9' : 'rgba(255,255,255,0.96)', border: '1px solid rgba(255,255,255,0.72)', borderRadius: '15px', boxShadow: '0 8px 22px rgba(30,64,99,0.16)', cursor: 'pointer' }}>
                  <SlidersHorizontal size={19} aria-hidden="true" />
                  {activeFilterCount > 0 && <span style={{ position: 'absolute', top: '-5px', right: '-5px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '19px', height: '19px', padding: '0 4px', color: '#ffffff', background: SECTION_THEME.events.accent, border: '2px solid #ffffff', borderRadius: '999px', fontSize: '9px', fontWeight: '800' }}>{activeFilterCount}</span>}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: isDesktop ? '1360px' : '1200px', margin: '0 auto', padding: '18px 16px 0' }}>
        {isDemoMode && <div role="status" style={{ marginBottom: '14px', padding: '10px 14px', color: '#9a5b08', background: '#fff7e8', border: '1px solid #f7d79b', borderRadius: '14px', fontSize: '13px', fontWeight: '700', textAlign: 'center' }}>測試預覽：目前顯示 public/test event 的三筆範例活動，不會寫入正式資料庫。</div>}

        {isDesktop && renderDesktopFilterBar()}

        {data?.discoveryError && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '11px 14px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', fontSize: '13px', fontWeight: '650' }}>
            <AlertCircle size={16} aria-hidden="true" />
            {data.discoveryError}
          </div>
        )}

        {isLoading && events.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '360px', color: '#526579' }}><div style={{ textAlign: 'center' }}><div style={{ width: '38px', height: '38px', margin: '0 auto 14px', border: '4px solid #eef4f9', borderTop: `4px solid ${SECTION_THEME.events.accent}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />載入活動中...</div></div>
        ) : filteredEvents.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '330px', padding: '48px 24px', background: '#ffffff', border: '1px solid #dbe4ee', borderRadius: '24px', boxShadow: '0 10px 32px rgba(30,64,99,0.08)', textAlign: 'center' }}>
            <Calendar size={58} color="#b7cbe0" />
            <h3 style={{ margin: '16px 0 8px', color: '#334155', fontSize: '1.2rem', fontWeight: '800' }}>{events.length === 0 ? '還沒有活動' : '找不到符合條件的活動'}</h3>
            <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '14px' }}>{events.length === 0 ? '成為第一個發起活動的人吧！' : '試試其他條件，或清除篩選。'}</p>
            <button type="button" onClick={events.length === 0 ? () => navigate('/events/create') : clearFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '12px 22px', color: '#ffffff', background: SECTION_THEME.events.accent, border: 'none', borderRadius: '14px', fontSize: '14px', fontWeight: '800', cursor: 'pointer' }}>{events.length === 0 ? <Plus size={17} /> : <X size={17} />}{events.length === 0 ? '發起活動' : '清除篩選'}</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '30px' }}>
            {recommendedEvents.length > 0 && (
              <section aria-labelledby="recommended-events-title">
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '13px', padding: '0 3px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', color: '#ffffff', background: SECTION_THEME.events.accent, borderRadius: '11px', boxShadow: '0 6px 14px rgba(78,139,202,0.24)' }}><BadgeCheck size={18} /></span>
                  <div><h2 id="recommended-events-title" style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '850' }}>官方推薦</h2><p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '11px' }}>JoinU 精選的活動與合作企劃</p></div>
                </div>
                <div style={gridStyle}>{recommendedEvents.map(event => renderEventCard(event, { isRecommended: true, recommendationType: event.recommendationType }))}</div>
              </section>
            )}

            {trendingEvents.length > 0 && (
              <section aria-labelledby="trending-events-title">
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '13px', padding: '0 3px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', color: '#ffffff', background: '#ef4444', borderRadius: '11px', boxShadow: '0 6px 14px rgba(239,68,68,0.22)' }}><Flame size={18} /></span>
                  <div><h2 id="trending-events-title" style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '850' }}>近期熱門</h2><p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '11px' }}>最近7天報名成長最快的活動</p></div>
                </div>
                <div style={gridStyle}>{trendingEvents.map(event => renderEventCard(event, { isPopular: true }))}</div>
              </section>
            )}

            {upcomingEvents.length > 0 && (
              <section aria-labelledby="upcoming-events-title">
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '13px', padding: '0 3px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', color: SECTION_THEME.events.accent, background: '#eef4f9', borderRadius: '11px' }}><Sparkles size={17} /></span>
                  <h2 id="upcoming-events-title" style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '850' }}>近期活動</h2>
                </div>
                <div style={{ display: 'grid', gap: '24px' }}>
                  {groupEventsByTime(upcomingEvents).map(section => (
                    <div key={section.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}><h3 style={{ margin: 0, color: '#334155', fontSize: '13px', fontWeight: '800' }}>{section.label}</h3><span style={{ padding: '2px 8px', color: SECTION_THEME.events.accent, background: '#eef4f9', borderRadius: '999px', fontSize: '10px', fontWeight: '800' }}>{section.events.length}</span></div>
                      <div style={gridStyle}>{section.events.map(event => renderEventCard(event))}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <button type="button" aria-label="發起活動" onClick={() => navigate('/events/create')} style={{ position: 'fixed', right: '20px', bottom: '84px', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '54px', height: '54px', color: '#ffffff', background: SECTION_THEME.events.accent, border: 'none', borderRadius: '50%', boxShadow: '0 8px 20px rgba(78,139,202,0.36)', cursor: 'pointer' }}><Plus size={25} /></button>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Mobile Filter Modal */}
      <FullScreenFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="篩選活動"
        onClear={activeFilterCount > 0 ? clearFilters : undefined}
        onApply={() => setIsFilterModalOpen(false)}
        applyLabel={`顯示 ${filteredEvents.length} 筆結果`}
      >
        {mobileFilterContent}
      </FullScreenFilterModal>

      {/* City Selection Modal */}
      <FullScreenFilterModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        title="選擇縣市"
        onClear={selectedCities.length > 0 ? () => setSelectedCities([]) : undefined}
        onApply={() => setIsCityModalOpen(false)}
        applyLabel="確定"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button
            onClick={() => { setSelectedCities([]); setIsCityModalOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px', background: 'transparent', border: 'none',
              borderBottom: '1px solid #f1f5f9', cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: selectedCities.length === 0 ? '700' : '500', color: selectedCities.length === 0 ? '#1e293b' : '#64748b' }}>全台灣 (不限縣市)</span>
            {selectedCities.length === 0 && <Check size={20} color={SECTION_THEME.events.accent} />}
          </button>
          {TAIWAN_CITIES.map(city => {
            const isSelected = selectedCities.includes(city);
            return (
              <button
                key={city}
                onClick={() => setSelectedCities(prev => isSelected ? prev.filter(c => c !== city) : [...prev, city])}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px', background: 'transparent', border: 'none',
                  borderBottom: '1px solid #f1f5f9', cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Globe2 size={18} color={isSelected ? SECTION_THEME.events.accent : '#94a3b8'} />
                  <span style={{ fontSize: '16px', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#1e293b' : '#64748b' }}>{city}</span>
                </div>
                {isSelected && <Check size={20} color={SECTION_THEME.events.accent} />}
              </button>
            );
          })}
        </div>
      </FullScreenFilterModal>
    </div>
  );
}
